import type { ComponentType, ReactNode } from 'react';

import { noop } from 'lodash-es';

import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import type { SearchSuggestSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/search_suggest';
import type { MetadataFieldOptionsSchema } from '@iconik-io/iconik-api/src/services/metadata';
import type {
  httpPostSearchSuggest,
  SearchSearchSuggestSchema,
} from '@iconik-io/iconik-api/src/services/search';
import type { MetadataFieldType } from '@iconik-io/iconik-api/src/types/metadata_field_type';
import type { AriaProps } from '@iconik-io/shared-lib/src/react/a11y/aria';

import CheckboxFieldWrapper from '../../ui/forms/field_wrappers/checkbox/checkbox';
import SimpleDateFieldWrapper from '../../ui/forms/field_wrappers/date/date_simple';
import DateTimeFieldWrapper from '../../ui/forms/field_wrappers/date_time/date_time';
import SelectFieldWrapper from '../../ui/forms/field_wrappers/select/select';
import SelectFieldMultiWrapper from '../../ui/forms/field_wrappers/select_multi/select_multi';
import TextFieldWrapper from '../../ui/forms/field_wrappers/text/text';
import TextAreaFieldWrapper from '../../ui/forms/field_wrappers/textarea/textarea';
import UrlFieldWrapper from '../../ui/forms/field_wrappers/url/url';
import Email from '../../ui/forms/fields/email/email';
import { replaceNonInteger } from '../../ui/forms/utils/replace_non_integer';
import type { TagSelectOption } from '../../ui/select_field/types';
import { resolveRemoteResponse } from '../../ui/select_field/utils';
import { parseBooleanValue } from '../../utils/metadata/parse_boolean_value';
import type { BulkUpdateMode as BulkUpdateModeValue } from '../field_bulk_update_mode/field_bulk_update_mode';
import Separator from '../fields/separator/separator';
import validationErrors from '../fields/validation_errors';
import SelectFieldTagCloudValue from '../select_field_custom_values/select_field_tag_cloud_value/select_field_tag_cloud_value';
import SelectFieldTagValue from '../select_field_custom_values/select_field_tag_value/select_field_tag_value';

export const METADATA_EDIT_TRANSITION_TIME = 150;
const INVALID_VALUE_MESSAGE = 'No longer in select list';

export type MetadataTagCloudSelectValue = {
  value: string;
  score?: number;
};

export type MetadataRendererFieldValue = {
  value: string;
  label?: string;
};

export type MetadataRendererFieldProps = {
  label?: string;
  labelWidth?: string;
  labelComponent?: ComponentType<any>;
  updateModeComponent?: ComponentType<any>;
  modes?: BulkUpdateModeValue[];
  id: string;
  name: string;
  value?: MetadataRendererFieldValue[];
  vertical: boolean;
  options?: MetadataFieldOptionsSchema[];
  multi: boolean;
  required: boolean;
  sortable: boolean;
  help?: string;
  readOnly: boolean;
  aria?: AriaProps;
  onChange?: (value: unknown) => void;
  onFocus?: () => void;
  onAfterValidation?: () => void;
  hideIfNotSet: boolean;
  // Not specified in admin metadata view edit
  getSearchSuggestions?: (
    body: SearchSearchSuggestSchema,
  ) => Promise<EndpointResponse<ReturnType<typeof httpPostSearchSuggest>>>;
  dateFormat: string;
  dateTimeFormat: string;
  content?: ReactNode;
  className?: string;
  modifierValues?: Record<string, unknown>;
  onModifierValueChange?: (key: string, value: unknown) => void;
  onValuesCopied?: () => void;
  onCopyError?: (message: string) => void;
  withCheckboxSelection?: boolean;
  labelClassName?: string;
  labelAdornment?: ReactNode;
  minValue?: number | null;
  maxValue?: number | null;
};

export type MetadataFieldRenderer = (
  // A field type the frontend does not know falls through to the text field
  // below, which is also what an absent one should render.
  type: MetadataFieldType | undefined,
  fieldProps: MetadataRendererFieldProps,
) => ReactNode;

const metadataFieldRenderer: MetadataFieldRenderer = (
  fieldType,
  fieldProps,
) => {
  const separatorType = '__separator__';
  const props: MetadataRendererFieldProps & {
    validationErrors?: typeof validationErrors;
  } = { ...fieldProps };
  const { value, dateFormat, dateTimeFormat } = props;
  const valueArr = value || [];
  const singleValue =
    valueArr.length > 0 && valueArr[0] ? valueArr[0].value : undefined;
  const arrayValue = Array.isArray(valueArr) ? valueArr : [];
  let type = fieldType;
  if (fieldProps.name === separatorType) {
    type = separatorType;
  }
  props.validationErrors = validationErrors;

  if (!props.onChange) {
    props.onChange = noop;
  }
  const readOnly = Boolean(props.readOnly);

  switch (type) {
    case separatorType:
      return <Separator content={props.content ?? props.label} />;
    case 'string':
      return (
        <TextFieldWrapper
          {...props}
          value={singleValue}
          onChange={props.onChange}
        />
      );
    case 'email':
      return <Email {...props} value={singleValue} onChange={props.onChange} />;
    case 'text':
      return (
        <TextAreaFieldWrapper
          {...props}
          value={singleValue}
          onChange={props.onChange}
        />
      );
    case 'tag_cloud': {
      const { getSearchSuggestions } = props;
      const loadOptions = getSearchSuggestions
        ? (query: string) =>
            getSearchSuggestions({
              query,
              field_name: props.name.startsWith('metadata.')
                ? props.name
                : `metadata.${props.name}`,
            }).then(
              resolveRemoteResponse((response) =>
                response.body.objects.flatMap((suggestion) =>
                  suggestion.value ? [{ value: suggestion.value }] : [],
                ),
              ),
            )
        : undefined;
      const processNewOptionValue = (option: TagSelectOption) => ({
        ...option,
        label: option.label.trim(),
      });
      return (
        <SelectFieldMultiWrapper<
          MetadataTagCloudSelectValue,
          SearchSuggestSchemaPatched,
          TagSelectOption
        >
          className={props.className}
          id={props.id}
          name={props.name}
          label={props.label}
          labelWidth={props.labelWidth}
          labelComponent={props.labelComponent}
          updateModeComponent={props.updateModeComponent}
          modifierValues={props.modifierValues}
          labelClassName={props.labelClassName}
          aria={props.aria}
          labelAdornment={props.labelAdornment}
          onChange={props.onChange}
          onFocus={props.onFocus}
          required={props.required}
          help={props.help}
          readOnly={readOnly}
          canClear
          isMulti
          isCreatorMode
          valueComponent={SelectFieldTagCloudValue}
          loadOptions={loadOptions}
          value={arrayValue}
          optionSourceToSelect={(v) => ({
            value: (v.value ?? '').trim(),
            label: (v.value ?? '').trim(),
          })}
          valueSourceToSelect={(v) => ({
            score: v.score,
            value: (v.value ?? '').trim(),
            label: (v.value ?? '').trim(),
          })}
          valueSelectToSource={(v) => ({
            score: v.score ?? 1,
            // Trim so whitespace-only variants (e.g. " ada" vs "ada") don't
            // become distinct tags and duplicate.
            value: v.label.trim(),
          })}
          optionsListDelayMs={METADATA_EDIT_TRANSITION_TIME}
          hideOptionsList={readOnly}
          processNewOptionValue={processNewOptionValue}
          withCheckboxSelection
          enableValueCopyPaste
          onValuesCopied={props.onValuesCopied}
          onCopyError={props.onCopyError}
        />
      );
    }
    case 'drop_down': {
      const options = props.options ? [...props.options] : [];
      const optionsMap = Object.fromEntries(
        options.map((option) => [option.value, option]),
      );

      const dropDownValue = !props.multi
        ? singleValue
        : arrayValue.map((v) => {
            // Refreshing value labels with option labels we have
            const valueExistsInOptions = optionsMap[v.value];
            const option = v.value ? optionsMap[v.value] : undefined;
            const label = option ? option.label : v.label;
            return {
              ...v,
              // Fallback to value if label is undefined
              label: label ?? v.value,
              warningLabel: valueExistsInOptions
                ? undefined
                : INVALID_VALUE_MESSAGE,
            };
          });
      let warning;
      // For the single select mode fake the option if it doesn't exist to show the value as a label
      if (
        dropDownValue &&
        !Array.isArray(dropDownValue) &&
        !optionsMap[dropDownValue]
      ) {
        options.push({
          label: dropDownValue,
          value: dropDownValue,
        });
        warning = {
          warningLabel: INVALID_VALUE_MESSAGE,
          flaggedValue: dropDownValue,
        };
      }

      return (
        <SelectFieldWrapper
          className={props.className}
          id={props.id}
          name={props.name}
          label={props.label}
          labelWidth={props.labelWidth}
          labelComponent={props.labelComponent}
          updateModeComponent={props.updateModeComponent}
          modifierValues={props.modifierValues}
          labelClassName={props.labelClassName}
          labelAdornment={props.labelAdornment}
          aria={props.aria}
          onChange={(fieldValue) => {
            const newValue =
              props.multi && Array.isArray(fieldValue)
                ? fieldValue.map((item) => ({ value: item.value }))
                : fieldValue;

            props.onChange?.(newValue);
          }}
          onFocus={props.onFocus}
          required={props.required}
          help={props.help}
          readOnly={readOnly}
          canClear
          clearValue={null}
          isMulti={props.multi}
          valueComponent={SelectFieldTagValue}
          warning={warning}
          // `isMulti` is a runtime boolean, so the single and multi value
          // shapes cannot be told apart here. Splitting the two arms means
          // duplicating this element and its twenty props.
          // biome-ignore lint/plugin: see above.
          value={dropDownValue as any}
          options={options}
          validations={props.required ? 'isEmptyArray' : undefined}
          optionsListDelayMs={METADATA_EDIT_TRANSITION_TIME}
          valueSourceToSelect={(v: any) => ({
            value: v.value,
            label: v.label,
            warningLabel: v.warningLabel,
          })}
          valueSelectToSource={(v) => ({
            value: v.value,
            label: v.label,
          })}
          hideOptionsList={readOnly}
          withCheckboxSelection={props.multi}
          enableValueCopyPaste={props.multi}
          onValuesCopied={props.onValuesCopied}
          onCopyError={props.onCopyError}
        />
      );
    }
    case 'date':
      return (
        <SimpleDateFieldWrapper
          {...props}
          outputFormat="yyyy-MM-dd"
          value={singleValue}
          onChange={props.onChange}
          dateFormat={dateFormat}
          isRangeMode={false}
        />
      );
    case 'datetime':
      return (
        <DateTimeFieldWrapper
          {...props}
          value={singleValue}
          onChange={props.onChange}
          dateFormat={dateTimeFormat}
          isRangeMode={false}
        />
      );
    case 'boolean':
      return (
        <CheckboxFieldWrapper
          {...props}
          required={false}
          value={parseBooleanValue(singleValue)}
          onChange={props.onChange}
        />
      );
    case 'integer': {
      const strValue = singleValue ? String(singleValue) : singleValue;
      return (
        <TextFieldWrapper
          {...props}
          value={strValue}
          filterValue={(val) =>
            String(val).includes('-') // explicitly casting value to string in case of number value
              ? `-${replaceNonInteger(String(val))}`
              : replaceNonInteger(String(val))
          }
          validations={{
            minNumber: fieldProps.minValue ?? undefined,
            maxNumber: fieldProps.maxValue ?? undefined,
          }}
          onChange={props.onChange}
        />
      );
    }
    case 'float': {
      // Todo enforce min-max when enabled
      const strValue = singleValue ? String(singleValue) : singleValue;
      return (
        <TextFieldWrapper
          {...props}
          value={strValue}
          filterValue={(val) =>
            String(val).includes('-') // explicitly casting value to string in case of number value
              ? `-${String(val).replace(/[^0-9.,]/g, '')}`
              : String(val).replace(/[^0-9.,]/g, '')
          }
          onChange={props.onChange}
          inputMode="decimal"
          type="number"
          validations={{
            minNumber: fieldProps.minValue ?? undefined,
            maxNumber: fieldProps.maxValue ?? undefined,
          }}
        />
      );
    }
    case 'url':
      return (
        <UrlFieldWrapper
          {...props}
          value={arrayValue}
          onChange={props.onChange}
          validations={props.required ? 'isEmptyArray' : undefined}
        />
      );

    default:
      return (
        <TextFieldWrapper
          {...props}
          value={singleValue}
          onChange={props.onChange}
        />
      );
  }
};

export default metadataFieldRenderer;
